import { isIsolatedCloud } from '@atlaskit/atlassian-context';
import {
	isPostOfficePlacementsGloballyDisabled,
	useStableObject,
} from '@atlassian/post-office-context';
import { MaybePlacementFetchingProvider } from '@atlassian/post-office-placement-data/placement-fetcher-provider';

import { ERROR_BOUNDARY_LOCATION } from './constants';
import { useComponentErrorHandling } from './hooks/useComponentError';
import { PerformanceTracking, StableProviders } from './providers';
import {
	type ContextWithRoute,
	type ErrorBoundariedProps,
	type PlacementComponent,
	type PlacementComponentPropsWithRoute,
	type PlacementConfigurationOptions,
} from './types';
import { type ErrorBoundaryAdditionalAnalytics, withErrorBoundary } from '../withErrorBoundary';
import { type Fetcher } from '@atlassian/post-office-placement-data';
import { type FC, type ReactNode } from 'react';
import { type ErrorBoundaryPropsWithRender } from 'react-error-boundary';
import { type OnPerformanceTrackingEvent } from './hooks/usePerformanceTracking/types';

export const createBasePlacementComponent = <P,>(
	placementId: string,
	Component: PlacementComponent<P>,
	placementConfigurationOptions?: PlacementConfigurationOptions,
): FC<
	Partial<ErrorBoundaryPropsWithRender> &
		ErrorBoundaryAdditionalAnalytics & {
			context: ContextWithRoute;
			initialData: Promise<Response | undefined> | undefined;
			fetch?: Fetcher;
			onEvent?: OnPerformanceTrackingEvent;
		} & P
> =>
	withErrorBoundary<PlacementComponentPropsWithRoute<P>>((props) => {
		// Hide placement in Isolated Cloud environments if configured
		// The typeof document check ensures we don't call isIsolatedCloud() during SSR
		if (
			placementConfigurationOptions?.hideInIsolatedCloud &&
			typeof document !== 'undefined' &&
			isIsolatedCloud()
		) {
			return null;
		}

		const { context: contextWithRoute, onEvent, initialData, fetch, ...componentProps } = props;

		const { postOfficeRoute: route, ...context } = contextWithRoute;

		if (isPostOfficePlacementsGloballyDisabled(context)) {
			return null;
		}

		const stableContext = useStableObject(context);

		return (
			<PerformanceTracking initialData={initialData} onEvent={onEvent}>
				<MaybePlacementFetchingProvider fetch={fetch?.fetch}>
					<StableProviders
						route={route}
						context={stableContext}
						placement={{ placementId }}
						choreographer={placementConfigurationOptions?.choreographer}
						subscription={placementConfigurationOptions?.subscription}
					>
						<Component
							context={stableContext}
							initialData={initialData}
							{...(componentProps as unknown as P)}
						/>
					</StableProviders>
				</MaybePlacementFetchingProvider>
			</PerformanceTracking>
		);
	});

export const createPlacementComponent = <P,>(
	placementId: string,
	Component: PlacementComponent<P>,
	placementConfigurationOptions?: PlacementConfigurationOptions,
) => {
	const placement = createBasePlacementComponent(
		placementId,
		Component,
		placementConfigurationOptions,
	);

	return (props: ErrorBoundariedProps<P>): ReactNode => {
		const { onError, fallbackRender } = useComponentErrorHandling(props);

		return placement({
			...props,
			errorBoundaryLocation: ERROR_BOUNDARY_LOCATION,
			placementId,
			onError,
			fallbackRender,
		});
	};
};
